import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Phone, User, Calendar, Hash, Pill, FlaskConical, Heart,
  Stethoscope, Activity, Loader2, FileText, BookOpen, ChevronDown, ChevronRight,
  Wind, Thermometer, Scale, Gauge, Droplet, Ruler, AlertTriangle, Sparkles,
  ShieldAlert, HeartPulse, Plus, X, Check, Edit3,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { formatDateIST, formatDateTimeIST } from '../utils/date';
import { useTenant } from '../contexts/TenantContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Full-page patient profile. Aggregates demographics + every consultation
 * (vitals, diagnosis, Rx, investigations, treatment plan) from
 * `/api/patients/{id}/profile`. Three tabs:
 *   - Visits — chronological visit accordion with expand-to-see-detail
 *   - Vitals — trend cards + sparklines (re-uses the design language of
 *              VitalsHistoryDrawer)
 *   - Clinical — distinct diagnoses + frequently-prescribed medicines
 */
export default function PatientProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const brand = tenant?.branding?.primary_color || '#0f766e';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('visits');
  // Local optimistic state for editable header tags (allergies, conditions, blood group).
  const [allergies, setAllergies] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [bloodGroup, setBloodGroup] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/patients/${id}/profile`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
        setAllergies(data?.patient?.allergies || []);
        setConditions(data?.patient?.chronic_conditions || []);
        setBloodGroup(data?.patient?.blood_group || '');
      })
      .catch((e) => { if (!cancelled) console.error('profile load failed', e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const saveTags = async (next) => {
    try {
      await axios.patch(`${API}/api/patients/${id}/tags`, next, { withCredentials: true });
    } catch (e) {
      console.error('Tag save failed', e);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-zinc-400" />
          <p className="text-sm text-zinc-500 mt-2">Loading profile…</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <p className="text-sm text-zinc-500">Patient not found</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-3" data-testid="profile-back-btn">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
          </Button>
        </div>
      </div>
    );
  }

  const { patient, summary, visits } = data;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="text-white" style={{ background: `linear-gradient(135deg, ${brand} 0%, ${brand}cc 60%, ${brand}88 100%)` }}>
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-start gap-4">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            size="sm"
            className="bg-white/10 hover:bg-white/25 text-white border-white/20 mt-1"
            data-testid="profile-back-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
          </Button>
          <div className="h-14 w-14 rounded-2xl bg-white/20 ring-2 ring-white/40 flex items-center justify-center font-extrabold text-2xl flex-shrink-0" style={{ fontFamily: 'Manrope' }}>
            {(patient.name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] opacity-80">Patient Profile</div>
            <h1 className="text-2xl font-extrabold tracking-tight truncate" style={{ fontFamily: 'Manrope' }} data-testid="profile-name">
              {patient.name}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs opacity-95">
              <span className="inline-flex items-center gap-1"><Hash className="h-3 w-3" /> MRN {patient.mrn}</span>
              {patient.age && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {patient.age}y {patient.gender}</span>}
              {patient.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {patient.phone}</span>}
              {patient.last_visit_at && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Last seen {formatDateIST(patient.last_visit_at)}</span>}
              <BloodGroupEditor
                value={bloodGroup}
                onChange={(bg) => { setBloodGroup(bg); saveTags({ blood_group: bg }); }}
              />
            </div>
            {/* Allergy + Condition tag rows */}
            <div className="mt-3 space-y-1.5">
              <TagRow
                label="Allergies"
                icon={<ShieldAlert className="h-3 w-3" />}
                items={allergies}
                onAdd={(v) => {
                  const next = [...allergies, v];
                  setAllergies(next);
                  saveTags({ allergies: next });
                }}
                onRemove={(v) => {
                  const next = allergies.filter(a => a !== v);
                  setAllergies(next);
                  saveTags({ allergies: next });
                }}
                addPlaceholder="e.g. Penicillin"
                tone="red"
                testid="allergy"
              />
              <TagRow
                label="Chronic conditions"
                icon={<HeartPulse className="h-3 w-3" />}
                items={conditions}
                onAdd={(v) => {
                  const next = [...conditions, v];
                  setConditions(next);
                  saveTags({ chronic_conditions: next });
                }}
                onRemove={(v) => {
                  const next = conditions.filter(c => c !== v);
                  setConditions(next);
                  saveTags({ chronic_conditions: next });
                }}
                addPlaceholder="e.g. Hypertension"
                tone="amber"
                testid="condition"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary stat strip */}
      <div className="max-w-6xl mx-auto px-6 -mt-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Visits" value={summary.visits} color={brand} />
          <StatCard label="Last visit" value={summary.last_visit ? formatDateIST(summary.last_visit) : '—'} color="#0891b2" />
          <StatCard label="Diagnoses" value={summary.diagnoses_distinct} sub={summary.diagnoses_distinct === 1 ? 'condition tracked' : 'distinct'} color="#7c3aed" />
          <StatCard label="Medications" value={summary.prescriptions_total} sub={`${summary.investigations_total} investigations`} color="#dc2626" />
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full max-w-md grid-cols-3 mb-5">
            <TabsTrigger value="visits" data-testid="profile-tab-visits"><Calendar className="h-3.5 w-3.5 mr-1.5" /> Visits</TabsTrigger>
            <TabsTrigger value="vitals" data-testid="profile-tab-vitals"><Heart className="h-3.5 w-3.5 mr-1.5" /> Vitals</TabsTrigger>
            <TabsTrigger value="clinical" data-testid="profile-tab-clinical"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Clinical</TabsTrigger>
          </TabsList>

          <TabsContent value="visits" className="space-y-3">
            {visits.length === 0 ? (
              <Empty text="No visits recorded for this patient yet." />
            ) : (
              visits.map((v, i) => <VisitAccordion key={v.consultation_id || i} visit={v} brand={brand} defaultOpen={i === 0} />)
            )}
          </TabsContent>

          <TabsContent value="vitals">
            <VitalsTab visits={visits} brand={brand} />
          </TabsContent>

          <TabsContent value="clinical">
            <ClinicalTab visits={visits} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ---------- Stat card ----------
function StatCard({ label, value, sub, color }) {
  return (
    <div className="bg-white border rounded-xl p-3 shadow-sm relative overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: color }} />
      <div className="ml-1.5">
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</div>
        <div className="text-xl font-extrabold text-zinc-900 mt-0.5 leading-none" style={{ fontFamily: 'Manrope' }}>{value}</div>
        {sub && <div className="text-[10px] text-zinc-400 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="py-16 text-center text-zinc-400 bg-white rounded-xl border border-dashed">
      <FileText className="h-8 w-8 mx-auto opacity-40" />
      <p className="text-sm mt-2">{text}</p>
    </div>
  );
}

// ---------- Header: editable tag rows ----------

const TAG_TONES = {
  red:   { bg: 'bg-red-500/25',   ring: 'ring-red-200/60',   text: 'text-white' },
  amber: { bg: 'bg-amber-500/25', ring: 'ring-amber-200/60', text: 'text-white' },
};

function TagRow({ label, icon, items, onAdd, onRemove, addPlaceholder, tone, testid }) {
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');
  const t = TAG_TONES[tone] || TAG_TONES.red;
  const commit = () => {
    const v = value.trim();
    if (v) onAdd(v);
    setValue('');
    setAdding(false);
  };
  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { setValue(''); setAdding(false); }
  };

  return (
    <div className="flex items-start gap-2 flex-wrap" data-testid={`tag-row-${testid}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80 inline-flex items-center gap-1 mt-1.5">
        {icon}{label}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {items.length === 0 && !adding && (
          <span className="text-[11px] opacity-60 italic">none recorded</span>
        )}
        {items.map((it, i) => (
          <span
            key={i}
            className={`inline-flex items-center gap-1 ${t.bg} ${t.text} ring-1 ${t.ring} backdrop-blur-sm text-[11px] font-semibold px-2 py-0.5 rounded-full`}
            data-testid={`${testid}-pill-${i}`}
          >
            {it}
            <button
              onClick={() => onRemove(it)}
              className="rounded-full hover:bg-white/30 p-0.5 transition"
              aria-label={`Remove ${it}`}
              data-testid={`${testid}-remove-${i}`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        {adding ? (
          <div className="inline-flex items-center gap-1 bg-white/95 text-zinc-900 rounded-full px-1 py-0.5 ring-1 ring-white/60">
            <input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onKey}
              onBlur={commit}
              placeholder={addPlaceholder}
              className="bg-transparent text-[12px] px-2 py-0.5 w-44 focus:outline-none placeholder:text-zinc-400"
              data-testid={`${testid}-input`}
            />
            <button onClick={commit} className="rounded-full hover:bg-emerald-100 p-0.5" data-testid={`${testid}-confirm`}>
              <Check className="h-3 w-3 text-emerald-700" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/30 transition ring-1 ring-white/30 text-[11px] font-semibold px-2 py-0.5 rounded-full"
            data-testid={`${testid}-add`}
          >
            <Plus className="h-2.5 w-2.5" /> Add
          </button>
        )}
      </div>
    </div>
  );
}

const BLOOD_GROUPS = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];

function BloodGroupEditor({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center" data-testid="blood-group-editor">
      {value ? (
        <button
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1 bg-red-500/25 text-white ring-1 ring-red-200/60 text-[11px] font-bold px-2 py-0.5 rounded-full hover:bg-red-500/40 transition"
          data-testid="blood-group-pill"
        >
          <Droplet className="h-3 w-3" /> {value}
          <Edit3 className="h-2.5 w-2.5 opacity-60" />
        </button>
      ) : (
        <button
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/30 ring-1 ring-white/30 text-[11px] font-semibold px-2 py-0.5 rounded-full"
          data-testid="blood-group-add"
        >
          <Droplet className="h-3 w-3" /> Add blood group
        </button>
      )}
      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-10 bg-white text-zinc-900 rounded-lg shadow-xl border p-2 flex flex-wrap gap-1 min-w-[180px]"
          data-testid="blood-group-popover"
        >
          {BLOOD_GROUPS.map(bg => (
            <button
              key={bg}
              onClick={() => { onChange(bg); setOpen(false); }}
              className={`text-[11px] font-bold px-2 py-1 rounded ${value === bg ? 'bg-red-100 text-red-700' : 'bg-zinc-100 hover:bg-zinc-200'}`}
              data-testid={`bg-opt-${bg}`}
            >
              {bg}
            </button>
          ))}
          {value && (
            <button
              onClick={() => { onChange(''); setOpen(false); }}
              className="text-[10px] text-zinc-500 hover:text-red-600 px-2 py-1 w-full text-left mt-1 border-t"
              data-testid="bg-clear"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </span>
  );
}

// ---------- Visit accordion ----------
function VisitAccordion({ visit, brand, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const dx = visit.final_diagnosis || visit.provisional_diagnosis;
  const rx = visit.prescription || [];
  const inv = visit.investigations || [];
  const v = visit.vitals || {};
  const alarmBP = (v.bp_systolic >= 140) || (v.bp_diastolic >= 90);

  return (
    <div className="bg-white border rounded-xl overflow-hidden shadow-sm" data-testid={`profile-visit-${visit.consultation_id}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left border-l-4 hover:bg-zinc-50/60 transition"
        style={{ borderColor: brand }}
      >
        <div className="flex-shrink-0 text-center w-14">
          <div className="text-base font-extrabold text-zinc-900 leading-none" style={{ fontFamily: 'Manrope' }}>
            {visit.date ? new Date(visit.date).toLocaleDateString('en-IN', { day: '2-digit', timeZone: 'Asia/Kolkata' }) : '—'}
          </div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">
            {visit.date ? new Date(visit.date).toLocaleDateString('en-IN', { month: 'short', year: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-zinc-900 truncate">{visit.doctor_name || 'Doctor'}</span>
            {visit.status && (
              <span className="text-[10px] capitalize bg-zinc-100 text-zinc-700 border border-zinc-200 px-2 py-0.5 rounded-full">
                {visit.status.replace(/_/g, ' ')}
              </span>
            )}
            {visit.chief_complaint && (
              <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full truncate max-w-[260px]">
                <Stethoscope className="h-2.5 w-2.5 inline mr-0.5" />{visit.chief_complaint}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-1 flex-wrap">
            {visit.date && <span>{formatDateTimeIST(visit.date)}</span>}
            {rx.length > 0 && <span className="text-emerald-700">{rx.length} Rx</span>}
            {inv.length > 0 && <span className="text-blue-700">{inv.length} test{inv.length === 1 ? '' : 's'}</span>}
            {alarmBP && (
              <span className="inline-flex items-center gap-0.5 text-red-700">
                <AlertTriangle className="h-3 w-3" /> BP elevated
              </span>
            )}
          </div>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-zinc-100">
          <VitalsRow vitals={v} />
          {dx && (
            <Block icon={<BookOpen className="h-3 w-3" />} label={visit.final_diagnosis ? 'Final diagnosis' : 'Provisional diagnosis'}>
              <p className="text-sm text-zinc-800 leading-relaxed">{dx}</p>
            </Block>
          )}
          {rx.length > 0 && (
            <Block icon={<Pill className="h-3 w-3" />} label={`Prescription (${rx.length})`}>
              <div className="space-y-1.5">
                {rx.map((m, i) => (
                  <div key={i} className="flex items-baseline gap-2 text-sm flex-wrap">
                    <span className="font-semibold text-zinc-900">{m.name}</span>
                    {m.dose && <span className="text-zinc-500">· {m.dose}</span>}
                    {m.timing && <span className="text-zinc-700 font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">{m.timing}</span>}
                    {m.intake && <span className="text-zinc-500 text-xs">{m.intake}</span>}
                    {m.duration && <span className="text-zinc-700 text-xs">× {m.duration}</span>}
                    {m.notes && <span className="text-zinc-500 text-xs italic">— {m.notes}</span>}
                  </div>
                ))}
              </div>
            </Block>
          )}
          {inv.length > 0 && (
            <Block icon={<FlaskConical className="h-3 w-3" />} label={`Investigations (${inv.length})`}>
              <div className="flex flex-wrap gap-1.5">
                {inv.map((t, i) => (
                  <span key={i}
                    className={`inline-flex items-center text-[11px] px-2 py-1 rounded-md font-semibold border ${
                      t.priority === 'urgent' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'
                    }`} title={t.reason || ''}>
                    {t.name}{t.result ? ` · ${t.result}` : ''}
                  </span>
                ))}
              </div>
            </Block>
          )}
          {visit.treatment_plan && (
            <Block icon={<FileText className="h-3 w-3" />} label="Treatment plan">
              <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">{visit.treatment_plan}</p>
            </Block>
          )}
        </div>
      )}
    </div>
  );
}

function Block({ icon, label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5 flex items-center gap-1.5">
        {icon}{label}
      </div>
      {children}
    </div>
  );
}

function VitalsRow({ vitals }) {
  if (!vitals) return null;
  const chips = [
    { l: 'BP',   v: (vitals.bp_systolic && vitals.bp_diastolic) ? `${vitals.bp_systolic}/${vitals.bp_diastolic}` : null, alarm: (vitals.bp_systolic >= 140 || vitals.bp_diastolic >= 90) },
    { l: 'HR',   v: vitals.pulse, alarm: vitals.pulse && (vitals.pulse > 100 || vitals.pulse < 50) },
    { l: 'SpO₂', v: vitals.spo2 ? `${vitals.spo2}%` : null, alarm: vitals.spo2 && vitals.spo2 < 95 },
    { l: 'T',    v: vitals.temperature ? `${vitals.temperature}°` : null, alarm: vitals.temperature > 38 },
    { l: 'Wt',   v: vitals.weight ? `${vitals.weight}kg` : null },
    { l: 'BMI',  v: vitals.bmi, alarm: vitals.bmi && (vitals.bmi > 30 || vitals.bmi < 17) },
    { l: 'RBS',  v: vitals.rbs, alarm: vitals.rbs && vitals.rbs > 200 },
  ].filter(c => c.v != null && c.v !== '');
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((c, i) => (
        <span key={i} className={`text-[11px] px-2 py-1 rounded font-mono ${c.alarm ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-zinc-100 text-zinc-700'}`}>
          <span className="text-zinc-500 font-sans font-bold mr-1">{c.l}</span>{c.v}
        </span>
      ))}
    </div>
  );
}

// ---------- Vitals tab ----------
const VITAL_DEFS = [
  { mkey: 'bp_systolic', label: 'Blood pressure', unit: 'mmHg', icon: Heart, color: '#e11d48',
    alarm: v => (v.bp_systolic >= 140) || (v.bp_diastolic >= 90),
    format: v => (v.bp_systolic && v.bp_diastolic) ? `${v.bp_systolic}/${v.bp_diastolic}` : null,
    normal: '<140/90' },
  { mkey: 'pulse', label: 'Pulse', unit: 'bpm', icon: Activity, color: '#dc2626',
    alarm: v => v.pulse && (v.pulse > 100 || v.pulse < 50), format: v => v.pulse ?? null, normal: '60–100' },
  { mkey: 'spo2', label: 'SpO₂', unit: '%', icon: Wind, color: '#0891b2',
    alarm: v => v.spo2 && v.spo2 < 95, format: v => v.spo2 ?? null, normal: '≥ 95' },
  { mkey: 'temperature', label: 'Temperature', unit: '°C', icon: Thermometer, color: '#f59e0b',
    alarm: v => v.temperature && v.temperature > 38, format: v => v.temperature ?? null, normal: '36–37.5' },
  { mkey: 'weight', label: 'Weight', unit: 'kg', icon: Scale, color: '#7c3aed', format: v => v.weight ?? null },
  { mkey: 'bmi', label: 'BMI', unit: 'kg/m²', icon: Gauge, color: '#0d9488',
    alarm: v => v.bmi && (v.bmi > 30 || v.bmi < 17), format: v => v.bmi ?? null, normal: '18.5–24.9' },
  { mkey: 'rbs', label: 'Random sugar', unit: 'mg/dL', icon: Droplet, color: '#db2777',
    alarm: v => v.rbs && v.rbs > 200, format: v => v.rbs ?? null, normal: '< 200' },
  { mkey: 'height', label: 'Height', unit: 'cm', icon: Ruler, color: '#475569', format: v => v.height ?? null },
];

function VitalsTab({ visits, brand }) {
  // series oldest → newest, with vitals flat at the root
  const series = useMemo(() => {
    const ordered = [...visits].reverse();
    return ordered.map(v => ({ date: v.date, ...(v.vitals || {}) }));
  }, [visits]);

  if (series.length === 0) {
    return <Empty text="No vital readings recorded yet." />;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {VITAL_DEFS.map(d => <VitalTrendCard key={d.mkey} def={d} series={series} brand={brand} />)}
    </div>
  );
}

function VitalTrendCard({ def, series, brand }) {
  const Icon = def.icon;
  let latestRow = null;
  for (let i = series.length - 1; i >= 0; i--) {
    const r = series[i];
    const val = def.format ? def.format(r) : r[def.mkey];
    if (val !== null && val !== undefined && val !== '') { latestRow = r; break; }
  }
  const valueDisplay = latestRow ? (def.format ? def.format(latestRow) : latestRow[def.mkey]) : null;
  const hasValue = valueDisplay !== null && valueDisplay !== undefined && valueDisplay !== '';
  const isAlarm = (latestRow && def.alarm) ? def.alarm(latestRow) : false;
  const pts = series.map(r => Number(r[def.mkey])).filter(v => Number.isFinite(v) && v > 0);
  const color = def.color || brand;
  const stamp = latestRow?.date ? new Date(latestRow.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' }) : null;

  // sparkline path
  let path = '', area = '', coords = [], W = 110, H = 38;
  if (pts.length > 0) {
    const max = Math.max(...pts), min = Math.min(...pts);
    const range = Math.max(1, max - min);
    const stepX = pts.length > 1 ? W / (pts.length - 1) : W;
    coords = pts.map((v, i) => ({ x: i * stepX, y: H - ((v - min) / range) * (H - 6) - 3 }));
    path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
    area = `${path} L${W},${H} L0,${H} Z`;
  }
  const gradId = `pp-g-${def.mkey}`;

  return (
    <div className={`relative rounded-xl border bg-white overflow-hidden ${isAlarm ? 'ring-1 ring-red-300 border-red-200' : 'border-zinc-200'}`}>
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: isAlarm ? '#dc2626' : color }} />
      <div className="pl-4 pr-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
            <Icon className="h-3 w-3" style={{ color }} />{def.label}
          </div>
          {def.normal && (
            <span className="text-[9px] text-zinc-400" title="Normal range">{def.normal} {def.unit}</span>
          )}
        </div>
        <div className="flex items-end justify-between gap-3 mt-1.5">
          <div className="min-w-0">
            <div className={`text-2xl font-extrabold leading-none ${isAlarm ? 'text-red-700' : 'text-zinc-900'}`} style={{ fontFamily: 'Manrope' }}>
              {hasValue ? valueDisplay : <span className="text-zinc-300">—</span>}
            </div>
            <div className="text-[10px] text-zinc-400 mt-1 flex items-center gap-1 flex-wrap">
              <span>{def.unit}</span>
              {stamp && <span>· {stamp}</span>}
              {pts.length} reading{pts.length === 1 ? '' : 's'}
            </div>
          </div>
          {pts.length > 0 && (
            <svg width={W} height={H} className="flex-shrink-0">
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity="0.25" />
                  <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
              </defs>
              {area && <path d={area} fill={`url(#${gradId})`} />}
              {path && <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />}
              {coords.map((c, i) => (
                <circle key={i} cx={c.x} cy={c.y} r={i === coords.length - 1 ? 2.8 : 1.4} fill={color} />
              ))}
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Clinical insights tab ----------
function ClinicalTab({ visits }) {
  const dxFreq = {};
  const rxFreq = {};
  const invFreq = {};
  visits.forEach(v => {
    const dx = (v.final_diagnosis || v.provisional_diagnosis || '').trim();
    if (dx) dxFreq[dx] = (dxFreq[dx] || 0) + 1;
    (v.prescription || []).forEach(m => {
      const name = (m.name || '').trim();
      if (name) rxFreq[name] = (rxFreq[name] || 0) + 1;
    });
    (v.investigations || []).forEach(t => {
      const name = (t.name || '').trim();
      if (name) invFreq[name] = (invFreq[name] || 0) + 1;
    });
  });
  const sort = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]);
  const dx = sort(dxFreq);
  const rx = sort(rxFreq);
  const inv = sort(invFreq);

  if (dx.length === 0 && rx.length === 0 && inv.length === 0) {
    return <Empty text="No clinical content captured yet — start a consultation to populate this section." />;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <FreqBlock title="Distinct diagnoses" items={dx} color="#7c3aed" icon={BookOpen} testid="clinical-diagnoses" />
      <FreqBlock title="Frequent medications" items={rx} color="#0d9488" icon={Pill} testid="clinical-medications" />
      <FreqBlock title="Investigations ordered" items={inv} color="#2563eb" icon={FlaskConical} testid="clinical-investigations" />
    </div>
  );
}

function FreqBlock({ title, items, color, icon: Icon, testid }) {
  return (
    <div className="bg-white border rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="h-7 w-7 rounded-lg flex items-center justify-center text-white" style={{ background: color }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-sm font-bold text-zinc-900">{title}</div>
        <span className="ml-auto text-[10px] text-zinc-400">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-zinc-400 italic py-2">None recorded.</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map(([name, count], i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span className="text-zinc-800 truncate flex-1 pr-2">{name}</span>
              <span className="text-[10px] text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-full font-semibold">
                {count}×
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
